/**
 * A person's own photos and video, stored on the server.
 *
 * Everything is scoped to the signed-in session — no call takes a user id, so
 * one person's media is not reachable by guessing an id. The `url` on a row is
 * an authenticated endpoint, not a public file path: it renders in an <img>
 * because the browser sends the session cookie, and returns 404 to anyone else.
 */

export type MediaOwnerKind = 'place' | 'person'

export interface MediaItem {
  media_id: string
  content_type: string
  bytes: number
  original_name: string
  caption: string
  created_at: string
  /** Authenticated endpoint — usable directly as an <img>/<video> src. */
  url: string
  is_video: boolean
}

async function readJson(res: Response, fallback: string) {
  const body = await res.json().catch(() => ({}))
  if (!res.ok || body?.success === false) throw new Error(body?.error || fallback)
  return body
}

export const mediaService = {
  async listFor(kind: MediaOwnerKind, ownerId: number): Promise<MediaItem[]> {
    const res = await fetch(`/api/media/for/${kind}/${ownerId}`, { credentials: 'include' })
    const body = await readJson(res, 'Could not load photos')
    return (body.data || []) as MediaItem[]
  },

  /**
   * Upload one file. Sent as multipart because the bytes are the payload —
   * base64 in JSON would inflate a video by a third for no gain.
   */
  async upload(
    kind: MediaOwnerKind,
    ownerId: number,
    file: File,
    caption = '',
  ): Promise<MediaItem> {
    const form = new FormData()
    form.append('file', file)
    form.append('owner_kind', kind)
    form.append('owner_id', String(ownerId))
    if (caption) form.append('caption', caption)
    const res = await fetch('/api/media', { method: 'POST', credentials: 'include', body: form })
    const body = await readJson(res, 'Could not save that file')
    return body.data as MediaItem
  },

  async remove(mediaId: string): Promise<void> {
    const res = await fetch(`/api/media/${mediaId}`, { method: 'DELETE', credentials: 'include' })
    await readJson(res, 'Could not remove that')
  },
}

/**
 * What the file picker offers — the server accepts exactly these.
 *
 * Images only (owner, 2026-09-08). The server refuses video independently, so
 * this list is convenience rather than the control: a picker can be worked
 * around, `media_store.ACCEPT_VIDEO` cannot.
 */
export const MEDIA_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp'

/** Human size, for a caption under a thumbnail. */
export function sizeWord(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
